import React, { ForwardedRef, useCallback, useEffect } from "react";
import { useTheme } from "styled-components";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { Box, useLoading } from "@monster/shared";
import { Button, Surface, TextField, Tooltip, TooltipContent, TooltipTrigger } from "@monster/chr-ui";
import { SVGChrIcon32BasicsQuestionMark } from "@monster/chr-ui/dist/svg_assets";
import { Validator } from "@utils/Validator";
import { FormHelpers } from "@utils/FormHelpers";
import { Loctool, LoctoolHTMLMessage, LoctoolMessage } from "@monster/loctool";
import { GraphQLClientError } from "@api/graphql/GraphQLClient";
import { GraphqlErrorMessage } from "@api/CustomTypes";
import { Path } from "@utils/Path";

type FormValues = {
    claimId: string;
};

type Props = {
    onSubmit: (result: FormValues) => Promise<void>;
    initialValues: FormValues;
    disabled: boolean;
};

export const ClaimIdForm = React.forwardRef((props: Props, ref: ForwardedRef<HTMLFormElement>) => {
    const theme = useTheme();
    const history = useHistory();
    const loading = useLoading();
    const {
        handleSubmit,
        register,
        setError,
        getValues,
        reset,
        formState: { errors },
    } = useForm<FormValues>({ defaultValues: props.initialValues, mode: "onTouched" });

    useEffect(() => {
        reset(props.initialValues);
    }, [props.initialValues, reset]);

    const onSubmit = useCallback(
        async (data: FormValues): Promise<void> => {
            loading.addLoading();
            try {
                await props.onSubmit(data);
            } catch (error) {
                if (error instanceof GraphQLClientError) {
                    if (error.message === GraphqlErrorMessage.claim_not_found) {
                        history.push(Path.customerCare);
                    } else {
                        setError("claimId", { message: error.intlMessage, type: "api" });
                    }
                }
            }
            loading.removeLoading();
        },
        [history, loading, props, setError]
    );

    if (props.disabled) {
        return (
            <Surface $variant="greyBorder">
                <Box as="dl" $style={{ margin: 0, padding: 16, "dd+dt": { marginTop: theme.text.bodyDesktop.lineHeight } }}>
                    <Box as="dt" $style={{ fontWeight: 700 }}>
                        <LoctoolMessage id="components.claimIdForm.claimId.label" />
                    </Box>

                    <Box as="dd" $style={{ margin: 0 }}>
                        {getValues().claimId}
                    </Box>
                </Box>
            </Surface>
        );
    }

    return (
        <Box ref={ref} as="form" $style={{ paddingBottom: 24 }} $styleLarge={{ paddingBottom: 0 }} onSubmit={handleSubmit(onSubmit)}>
            <Surface $variant="whiteArrowBottom" $styleLarge={{ boxShadow: theme.shadow.white, "&::after": { content: "normal" } }}>
                <Box $style={{ padding: "8px 16px 0" }}>
                    <TextField
                        id="ClaimIdInput"
                        label={<LoctoolMessage id="components.claimIdForm.claimId.label" />}
                        autoFocus={true}
                        placeholder={Loctool.instance.formatMessage({ id: "components.claimIdForm.claimId.placeholder" })}
                        {...register("claimId", { validate: Validator.claimId })}
                        errorText={FormHelpers.getError(errors.claimId)}
                        customSuffixElement={
                            <Box $style={{ pointerEvents: "all", position: "absolute", top: 8, right: 8, zIndex: 1 }}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button.Secondary tabIndex={-1} btnLabel={"info"} icon={<SVGChrIcon32BasicsQuestionMark />} size="xsmall" />
                                    </TooltipTrigger>
                                    <TooltipContent align="end" alignOffset={-16}>
                                        <Box $style={{ width: "calc(100vw - 3.5 * 16px)" }} $styleSmall={{ width: 320 }} $styleMedium={{ width: 374 }}>
                                            <LoctoolHTMLMessage id="components.claimIdForm.claimId.hint" />
                                        </Box>
                                    </TooltipContent>
                                </Tooltip>
                            </Box>
                        }
                    />
                </Box>

                <Box $style={{ maxWidth: 242 + 2 * 16, margin: "0 auto", padding: "24px 16px" }}>
                    <Button.Primary type="submit" btnLabel={<LoctoolMessage id="components.claimIdForm.btn.continue" />} isExpanded />
                </Box>

                <Surface className="hide-for-large" $variant="whiteArrowBottomShadow" />
            </Surface>
        </Box>
    );
});
